import { toAlgoliaFilters, toAlgoliaOptionalFilters } from '@shared/core/search/dialects/algolia';
import { z } from 'zod';

import { isAuthStatus, SearchAuthError, type SearchTransport } from './types';

const responseSchema = z.object({ hits: z.array(z.unknown()) });

const applicationId = (endpoint: string): string => {
  const match = /^https:\/\/([^./]+)-dsn\./i.exec(endpoint);
  if (!match?.[1]) throw new Error('Algolia endpoint carries no application id');
  return match[1];
};

export function createAlgoliaTransport(endpoint: string, key: string): SearchTransport {
  return {
    async search<TDoc>({
      descriptor,
      query,
      filter,
      hitsPerPage,
      boosts,
      signal,
    }: Parameters<SearchTransport['search']>[0]) {
      const filters = toAlgoliaFilters(filter);
      const url = new URL(`/1/indexes/${encodeURIComponent(descriptor.indexName.algolia)}/query`, endpoint);
      url.searchParams.set('x-algolia-application-id', applicationId(endpoint));
      url.searchParams.set('x-algolia-api-key', key);

      const params = new URLSearchParams({ query, hitsPerPage: String(hitsPerPage) });
      if (filters !== '') params.set('filters', filters);
      if (boosts !== undefined && boosts.length > 0) {
        params.set('optionalFilters', JSON.stringify(toAlgoliaOptionalFilters(boosts)));
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ params: params.toString() }),
        signal,
      });

      if (isAuthStatus(response.status)) throw new SearchAuthError(response.status);
      if (!response.ok) throw new Error(`Algolia responded ${response.status}`);
      return responseSchema.parse(await response.json()).hits as TDoc[];
    },
  };
}
